/**
 * MSP Agreement service — documents library (upload, table, preview, edit metadata).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import apiService from '../services/api';

const DOC_TYPES = [
  { value: 'agreement', label: 'Agreement' },
  { value: 'technical', label: 'Technical' },
  { value: 'other', label: 'Other' },
] as const;

function docTypeLabel(v: string): string {
  return DOC_TYPES.find((d) => d.value === v)?.label || v;
}

function formatFileSize(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export interface MspServiceDocumentsTabProps {
  serviceId: string;
}

const MspServiceDocumentsTab: React.FC<MspServiceDocumentsTabProps> = ({ serviceId }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addType, setAddType] = useState<string>('agreement');
  const [addFile, setAddFile] = useState<File | null>(null);
  const [addSaving, setAddSaving] = useState(false);

  const [editRow, setEditRow] = useState<any | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editType, setEditType] = useState('agreement');
  const [editSaving, setEditSaving] = useState(false);

  const [preview, setPreview] = useState<{ row: any; url: string; mime: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [deleteRow, setDeleteRow] = useState<any | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiService.getMspDocuments(serviceId);
      setRows(r.documents || []);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Failed to load documents');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const openPreview = async (row: any) => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const blob = await apiService.getMspDocumentFileBlob(serviceId, row.id, true);
      const mime = blob.type || row.content_type || 'application/octet-stream';
      const url = URL.createObjectURL(blob);
      setPreview({ row, url, mime });
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setPreviewLoading(false);
  };

  const submitAdd = async () => {
    if (!addTitle.trim() || !addFile) return;
    setAddSaving(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('title', addTitle.trim());
      fd.append('document_type', addType);
      fd.append('file', addFile);
      await apiService.uploadMspDocument(serviceId, fd);
      setAddOpen(false);
      setAddTitle('');
      setAddType('agreement');
      setAddFile(null);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setAddSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!editRow || !editTitle.trim()) return;
    setEditSaving(true);
    setErr(null);
    try {
      await apiService.updateMspDocument(serviceId, editRow.id, {
        title: editTitle.trim(),
        document_type: editType,
      });
      setEditRow(null);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Update failed');
    } finally {
      setEditSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteRow) return;
    setDeleteSaving(true);
    setErr(null);
    try {
      await apiService.deleteMspDocument(serviceId, deleteRow.id);
      setDeleteRow(null);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Documents
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Add document
        </Button>
      </Box>

      {err && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
          {err}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No documents yet. Upload agreements or reference files for this customer.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded by</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.uploaded_at ? new Date(row.uploaded_at).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{docTypeLabel(row.document_type)}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }} title={row.filename}>
                    <Typography variant="body2" noWrap>
                      {row.filename}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatFileSize(row.file_size_bytes)}</TableCell>
                  <TableCell>{row.uploaded_by_name || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="View" aria-label="View" onClick={() => openPreview(row)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Edit details"
                      aria-label="Edit details"
                      onClick={() => {
                        setEditRow(row);
                        setEditTitle(row.title);
                        setEditType(row.document_type || 'other');
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="Delete" aria-label="Delete" color="error" onClick={() => setDeleteRow(row)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={addOpen} onClose={() => !addSaving && setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add document</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Document title"
            fullWidth
            required
            value={addTitle}
            onChange={(e) => setAddTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel>Document type</InputLabel>
            <Select value={addType} label="Document type" onChange={(e) => setAddType(e.target.value)}>
              {DOC_TYPES.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label" fullWidth>
            Choose file
            <input
              type="file"
              hidden
              onChange={(e) => setAddFile(e.target.files?.[0] || null)}
            />
          </Button>
          {addFile && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {addFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={addSaving || !addTitle.trim() || !addFile}
            onClick={submitAdd}
          >
            {addSaving ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editRow} onClose={() => !editSaving && setEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit document</DialogTitle>
        <DialogContent>
          {editRow && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Uploaded {editRow.uploaded_at ? new Date(editRow.uploaded_at).toLocaleString() : '—'} — file cannot be replaced here
              </Typography>
              <TextField
                margin="dense"
                label="Document title"
                fullWidth
                required
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Document type</InputLabel>
                <Select value={editType} label="Document type" onChange={(e) => setEditType(e.target.value)}>
                  {DOC_TYPES.map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)} disabled={editSaving}>
            Cancel
          </Button>
          <Button variant="contained" disabled={editSaving || !editTitle.trim()} onClick={submitEdit}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteRow} onClose={() => !deleteSaving && setDeleteRow(null)}>
        <DialogTitle>Delete document</DialogTitle>
        <DialogContent>
          <Typography>Remove &quot;{deleteRow?.title}&quot;? The file will be permanently deleted.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleteSaving}>
            Cancel
          </Button>
          <Button color="error" variant="contained" disabled={deleteSaving} onClick={submitDelete}>
            {deleteSaving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewLoading || !!preview}
        onClose={() => {
          if (!previewLoading) closePreview();
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          {preview?.row?.title || 'Preview'}
        </DialogTitle>
        <DialogContent sx={{ p: 1, display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          {previewLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          )}
          {!previewLoading &&
            preview &&
            (preview.mime.startsWith('image/') ? (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto' }}>
                <img src={preview.url} alt={preview.row.title} style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 120px)' }} />
              </Box>
            ) : preview.mime.includes('pdf') ? (
              <iframe
                title={preview.row.title}
                src={preview.url}
                style={{ flex: 1, width: '100%', minHeight: 480, border: 'none' }}
              />
            ) : (
              <Box sx={{ py: 4 }}>
                <Typography gutterBottom>Preview is not available for this file type ({preview.mime}).</Typography>
                <Button variant="contained" href={preview.url} download={preview.row.filename} component="a">
                  Open / download
                </Button>
              </Box>
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} disabled={previewLoading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MspServiceDocumentsTab;
